import { ref, computed, Ref, toRefs, watch } from '@vue/composition-api';
import { AdgroupFormData, FullNormalFormData, ProductItem, VideoPackage } from '@tt4b/ttam-enums';
import { createLogger, sortJsonStringify, trackEvent } from '@tt4b/ttam-utils';
import {
  useRequest,
  useGetVideoPackageApi,
  useGetCatalogVideoInfoApi,
  usePreviewProductsApi,
  useGetPreviewMetaApi,
  PreviewProductsParams,
  useStore,
  useJumpCatalog,
  StateEnum,
  useGetProductWithSaveApi,
} from '@tt4b/ttam-hooks';
import { get } from 'lodash-es';

const logger = createLogger('CatalogsPreivew');
const currentProduct = ref<ProductItem>(null);

let getVideoPackagesPromise = Promise.resolve();

export const useSelectVideoPackage = (catalogId: Ref<string>, bcId: Ref<string>, templateId: Ref<string>) => {
  const { run, data, isLoading } = useRequest(useGetVideoPackageApi, null, { manual: true });
  const videoPackages = computed(() => data.value || []);
  const { state } = useStore();

  const curVideoPackage = computed(() => {
    logger.info('Choose videoPackage, User default tid=%s', templateId?.value);
    if (templateId.value) {
      return videoPackages.value?.find?.(pkg => pkg.packageId === templateId.value);
    }
    return null;
  });

  const _getVideoPackages = async () => {
    try {
      logger.info('Start preview catalog video, catalog=%s tid=%s', catalogId?.value, templateId?.value);
      if (catalogId.value && catalogId.value !== '0') {
        const params = { catalogId: catalogId.value, bcId: bcId.value };
        await run(params);
        state.videoPackageList.value = {
          ...state.videoPackageList.value,
          [sortJsonStringify(params)]: (data.value || []) as VideoPackage[],
        };
      } else {
        logger.warn('getVideoPackages, catalogId not found');
      }
    } catch (e) {
      logger.error(e);
    }
  };

  return {
    isLoading,
    videoPackages,
    curVideoPackage,
    getVideoPackages() {
      return (getVideoPackagesPromise = _getVideoPackages());
    },
  };
};

export function useGetVideoPackageByFormData(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { state } = useStore();

  const storeVkgKeys = computed(() =>
    sortJsonStringify({
      bcId: adgroupData.value?.catalogAuthorizedBc,
      catalogId: adgroupData.value?.productPlatformId,
    }),
  );

  const videoPackages = computed(() => state.videoPackageList.value?.[storeVkgKeys.value] || []);

  const videoPackagesLoaded = computed(() => Boolean(state.videoPackageList.value?.[storeVkgKeys.value]));

  return {
    getVideoPackagesPromise,
    videoPackagesLoaded,
    videoPackages,
  };
}

export const useGetPreviewProducts = (
  catalogId: Ref<string>,
  bcId: Ref<string>,
  pSetId?: Ref<string>,
  productIds?: Ref<string[]>,
) => {
  const remote = computed(() => !productIds?.value?.length);
  const { state } = useStore();

  const { run, data, isLoading } = useRequest(usePreviewProductsApi, null, { manual: true });

  const previewProductList = computed(() => data.value?.dpaProducts || []);

  const getPreviewProducts = async (otherParams?: PreviewProductsParams) => {
    const params = {
      catalogId: catalogId.value,
      bcId: bcId.value,
      productIds: productIds?.value?.filter(item => item),
      pSetId: pSetId?.value,
      ...otherParams,
    };
    logger.info('Get preview products, params=%s', params);
    if (catalogId.value && catalogId.value !== '0') {
      await run(params);
      state.previewProductList.value[sortJsonStringify(params)] = data.value?.dpaProducts || [];

      // 转化图片
      const needSaveProductIds = previewProductList.value
        .filter(item => !item?.image?.tosUri)
        .map(item => item.productID);
      if (needSaveProductIds.length > 0) {
        useGetProductWithSaveApi({
          catalogId: params.catalogId,
          bcId: params.bcId,
          productID: needSaveProductIds,
        });
      }
    } else {
      logger.warn('getPreviewProducts, catalogId not found');
    }
  };

  return {
    remote,
    isLoading,
    previewProductList,
    getPreviewProducts,
  };
};

export function useGetPreviewProductsByFormData(options: {
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
}) {
  const { adgroupData, creativeData } = options;
  const { state } = useStore();
  const previewProductList = computed(
    () =>
      state.previewProductList.value?.[
        sortJsonStringify({
          bcId: adgroupData.value.catalogAuthorizedBc,
          catalogId: adgroupData.value.productPlatformId,
          productIds: creativeData.value.dpaProducts,
          pSetId: creativeData.value.productSetId,
        })
      ],
  );
  return {
    storedPreviewProductList: state.previewProductList,
    previewProductList,
  };
}

export const useProductRenderImageMeta = (options: {
  catalogId: Ref<string>;
  bcId: Ref<string>;
  actionLabel: Ref<string>;
}) => {
  const { catalogId, bcId, actionLabel } = options;
  const productRenderImageMeta = ref();
  const previewProduct = ref();
  const productImageRenderError = ref(null);
  const { jumpProductPage, jumpVideoPage } = useJumpCatalog(catalogId, bcId);

  const setNoImageError = () => {
    logger.warn('No image error');
    productRenderImageMeta.value = null;
    productImageRenderError.value = {
      message: 'module_creative_preview_no_own_videos',
      cta: 'module_creative_preview_generate_video',
      ctaCb: jumpVideoPage,
    };
  };

  const setNoProductError = () => {
    logger.warn('No product error');
    productRenderImageMeta.value = null;
    productImageRenderError.value = {
      message: 'module_creative_preview_no_product',
      cta: 'common_action_add_product',
      ctaCb: jumpProductPage,
    };
  };

  const getProductRenderImageMeta = (productItem: ProductItem) => {
    previewProduct.value = productItem;
  };

  watch(previewProduct, () => {
    if (!previewProduct.value) {
      setNoProductError();
    } else if (!previewProduct.value.image) {
      setNoImageError();
    } else {
      productRenderImageMeta.value = {
        actionLabel: actionLabel.value ?? '',
        anchorLabel: '',
        type: 'image',
        media: {
          type: 'image',
          width: previewProduct.value.image.width ?? 0,
          height: previewProduct.value.image.height ?? 0,
          imageUrl: previewProduct.value.image.url ?? '',
        },
        price: previewProduct.value.price ?? '',
        productTitle: previewProduct.value.title,
      };
    }
  });

  return {
    productRenderImageMeta,
    getProductRenderImageMeta,
    productImageRenderError,
  };
};

export type ProductPreviewText = {
  tips: string;
  message: string;
} | null;

const handlePreviewChange = {
  useVideoUrl: (data: Ref<ProductPreviewText>) => {
    data.value = {
      tips: '',
      message: 'module_creative_preview_use_video_url',
    };
  },
  useTemplate: (data: Ref<ProductPreviewText>) => {
    data.value = {
      tips: 'module_creative_preview_use_video_template_tips',
      message: 'module_creative_preview_use_video_template',
    };
  },
  useNoTip: (data: Ref<ProductPreviewText>) => {
    data.value = null;
  },
};

export const useProductRenderMeta = (
  catalogId: Ref<string>,
  bcId: Ref<string>,
  videoPackage?: Ref<VideoPackage>,
  videoPackageList?: Ref<VideoPackage[]>,
) => {
  const productRenderMeta = ref();
  const productRenderError = ref(null);
  const productPreviewIndicate = ref<ProductPreviewText>(null);
  const isLoadingMeta = ref(false);

  const { jumpProductPage, jumpVideoPage } = useJumpCatalog(catalogId, bcId);
  const isSelectTemplate = computed(() => Boolean(videoPackage.value?.packageId));
  const curVideoPackage = computed(() => {
    if (isSelectTemplate.value) {
      return videoPackage.value;
    }
    const index = Math.floor(videoPackageList?.value?.length * Math.random());
    return videoPackageList?.value?.[index] || null;
  });
  const templateId = computed(() => curVideoPackage.value?.packageId);

  const setVideoData = async (params: { videoId: string; productId: string; bcId: string; catalogId: string }) => {
    const videoInfo = await useGetCatalogVideoInfoApi(params);
    productRenderMeta.value = {
      media: {
        type: 'video',
        vid: params.videoId,
        width: videoInfo.thumbWidth,
        height: videoInfo.thumbHeight,
        duration: videoInfo.videoDuration,
        posterUrl: videoInfo.imgUrl,
        playUrl: videoInfo.originPlayUrl,
      },
      videoInfo,
      productId: params.productId,
    };
    trackEvent('create_ad_creative_ready');
  };

  const setNoVideoError = () => {
    logger.warn('No video error');
    productRenderMeta.value = null;
    productRenderError.value = {
      message: 'module_cretive_preview_need_template_and_video_url',
      cta: 'module_creative_ad_creative_go_to_catalog',
      ctaCb: jumpVideoPage,
    };
  };

  const setNoProductError = () => {
    logger.warn('No product error');
    productRenderMeta.value = null;
    productRenderError.value = {
      message: 'module_creative_preview_no_product',
      cta: 'common_action_add_product',
      ctaCb: jumpProductPage,
    };
  };

  const setOtherError = () => {
    productRenderMeta.value = null;
    productRenderError.value = {
      message: 'module_creative_preview_gen_video_fail',
    };
  };

  const setPreviewMeta = async (productID: string) => {
    const previewMeta = await useGetPreviewMetaApi({
      productID,
      catalogId: catalogId.value,
      bcId: bcId.value,
      videoPackage: curVideoPackage.value,
    });
    productRenderMeta.value = {
      productRenderMeta: previewMeta,
      productId: productID,
    };
    trackEvent('create_ad_creative_ready');
  };

  const getProductRenderMeta = async (product: ProductItem) => {
    if (!product) {
      return;
    }
    try {
      logger.info('Get product render meta, pid=%s tid=%s', product.productID, templateId.value);
      currentProduct.value = product;
      productRenderError.value = null;
      isLoadingMeta.value = true;
      const commonCatalogVideoApiParams = {
        bcId: bcId.value,
        catalogId: catalogId.value,
        productId: product.productID,
      };
      handlePreviewChange.useNoTip(productPreviewIndicate);
      // 有自有视频并且没有视频包时
      if (product.video?.videoId && !isSelectTemplate.value) {
        logger.info('Set origin video data, vid=%s', product.video?.videoId);
        await setVideoData({
          ...commonCatalogVideoApiParams,
          videoId: product.video?.videoId,
        });
        handlePreviewChange.useVideoUrl(productPreviewIndicate);
      } else if (videoPackageList.value?.length > 0) {
        /** 提示用户用的是随机template进行预览 */
        if (!isSelectTemplate.value) {
          handlePreviewChange.useTemplate(productPreviewIndicate);
        }

        // 有视频包
        const video = product.videos?.find?.(v => v?.templateId === templateId.value);
        if (video?.videoId) {
          logger.info('Set video data, vid=%s', video?.videoId);
          await setVideoData({
            ...commonCatalogVideoApiParams,
            videoId: video.videoId,
          });
        } else {
          logger.info('Set video meta');
          // 上面没有生成视频，只能实时预览了
          await setPreviewMeta(product.productID);
        }
      } else {
        setNoVideoError();
      }
    } catch (e) {
      logger.warn('Set other error=%s', e?.message ?? e);
      setOtherError();
    } finally {
      isLoadingMeta.value = false;
    }
  };

  return {
    isLoadingMeta,
    productRenderError,
    setNoProductError,
    setNoVideoError,
    currentProduct,
    getProductRenderMeta,
    productPreviewIndicate,
    productRenderMeta: computed(() => {
      if (!productRenderError.value) {
        return productRenderMeta.value;
      }
      return {
        media: {
          type: 'video',
          vid: '',
        },
      };
    }),
  };
};

export function useGetCatalogTitle(options: {
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
}) {
  const { adgroupData, creativeData } = options;
  const { state } = useStore();
  const catalogWordList = computed(() => state[StateEnum.catalogLexiconList].data.value);
  const catalogId = computed(() => adgroupData.value.productPlatformId);
  const bcId = computed(() => adgroupData.value.catalogAuthorizedBc);
  const templateId = computed(() => creativeData.value.videoTplName);
  const { curVideoPackage } = useSelectVideoPackage(catalogId, bcId, templateId);
  const { currentProduct: curProduct } = useProductRenderMeta(catalogId, bcId, curVideoPackage);

  const replaceKeyWord = (match, $1) => {
    const catalogWord = catalogWordList.value?.find?.(item => item.name === $1);
    if (!catalogWord) {
      logger.warn('replace no word, match=%s $1=%s', match, $1);
      return match;
    }
    return get(curProduct.value, catalogWord.metaField, catalogWord.defaultValue);
  };

  const title = computed(() =>
    creativeData.value?.title?.replace?.(/\{\$(\w+)\$\}/g, replaceKeyWord)?.replace?.(/\[(\w+)\]/g, replaceKeyWord),
  );

  return {
    title,
  };
}
